import { createSignal, Show, createMemo, onMount } from "solid-js";
import { useUserContext } from "./UserContext";
import { getDefaultEmail } from "./utils";
import { useI18n } from "./i18n";

export default () => {
    const t = useI18n();
    const [userStore, setUserStore, userActions] = useUserContext();
    const [fullname, setFullname] = createSignal(userStore.fullname || "");
    const [studentNumber, setStudentNumber] = createSignal(userStore.student_number || "");
    const [healthInsurance, setHealthInsurance] = createSignal(
        userStore.additional_info?.healthInsurance || ""
    );
    
    // Create reactive memos for email-related values
    const isDefaultEmail = createMemo(() => !userStore.email);
    const defaultEmailValue = createMemo(() => getDefaultEmail(userStore.username, userStore.role));
    const [email, setEmail] = createSignal(userStore.email || defaultEmailValue());
    
    const [saving, setSaving] = createSignal(false);
    const [message, setMessage] = createSignal("");
    
    const saveProfile = () => {
        setSaving(true);
        setMessage("");
        
        // Only update fullname for students, admin users can update both fullname and email
        const updateData = userStore.role === 'student' 
            ? { 
                fullname: fullname(),
                student_number: studentNumber(),
                additional_info: {
                    ...userStore.additional_info,
                    healthInsurance: healthInsurance()
                }
              }
            : { fullname: fullname(), email: email() };
            
        userActions.updateUserProfile(updateData)
            .then(() => {
                setMessage("Profile updated successfully!");
                setSaving(false);
            })
            .catch(err => {
                setMessage("Error updating profile.");
                setSaving(false);
            });
    };
    
    const handleLeavePromo = () => {
        if (confirm(t("Are you sure you want to leave your current class? You'll need to select a new one before creating internship agreements."))) {
            userActions.leavePromo();
            setMessage(t("You've left your class successfully. You can select a new one from the home page."));
        }
    };
    
    return <div>
        <h1>{t("Settings")}</h1>
        
        <div class="card mb-4">
            <div class="card-header">
                {t("Profile Information")}
            </div>
            <div class="card-body">
                <div class="mb-3">
                    <label for="username" class="form-label">{t("Username")}</label>
                    <input type="text" class="form-control" id="username" value={userStore.username} disabled />
                    <div class="form-text">{t("Your login username cannot be changed.")}</div>
                </div>
                
                <div class="mb-3">
                    <label for="fullname" class="form-label">{t("Full Name")}</label>
                    <input 
                        type="text" 
                        class="form-control" 
                        id="fullname" 
                        value={fullname()} 
                        onInput={(e) => setFullname(e.target.value)}
                        placeholder={t("Enter your full name")}
                    />
                    <div class="form-text">{t("Your full name will be displayed to other users.")}</div>
                </div>
                
                <Show when={userStore.role === 'student'}>
                    <div class="mb-3">
                        <label for="studentNumber" class="form-label">{t("Student Number")}</label>
                        <input 
                            type="text" 
                            class="form-control" 
                            id="studentNumber" 
                            value={studentNumber()} 
                            onInput={(e) => setStudentNumber(e.target.value)}
                            placeholder={t("Enter your student number")}
                        />
                        <div class="form-text">{t("Your student number for administrative purposes.")}</div>
                    </div>
                    
                    <div class="mb-3">
                        <label for="healthInsurance" class="form-label">{t("Health Insurance - City + Region")}</label>
                        <input 
                            type="text" 
                            class="form-control" 
                            id="healthInsurance" 
                            value={healthInsurance()} 
                            onInput={(e) => setHealthInsurance(e.target.value)}
                            placeholder={t("e.g., CPAM Strasbourg - Grand Est")}
                        />
                        <div class="form-text">{t("Your health insurance fund with city and region.")}</div>
                    </div>
                </Show>
                
                <div class="mb-3">
                    <label for="email" class="form-label">{t("Email Address")}</label>
                    <div class="input-group">
                        <input 
                            type="email" 
                            class="form-control" 
                            id="email" 
                            value={email()} 
                            onInput={(e) => userStore.role !== 'student' && setEmail(e.target.value)}
                            placeholder="your.email@example.com" 
                            disabled={userStore.role === 'student'}
                        />
                        {isDefaultEmail() && 
                            <span class="input-group-text bg-secondary text-white" title={t("Default email based on username and role")}>
                                {t("default")}
                            </span>
                        }
                    </div>
                    <div class="form-text">
                        {userStore.role === 'student' 
                            ? t("Your email address cannot be changed. Contact an administrator if it needs to be updated.")
                            : isDefaultEmail() 
                                ? t("This is your default email based on your username and role. You can change it if needed.")
                                : t("Your email is used for notifications about feedback requests and validations.")
                        }
                    </div>
                </div>
                <button 
                    type="button" 
                    class="btn btn-primary" 
                    onClick={saveProfile}
                    disabled={saving()}
                >
                    {saving() ? t("Saving...") : t("Save Changes")}
                </button>
                
                <Show when={message()}>
                    <div class="alert alert-info mt-3">{message()}</div>
                </Show>
            </div>
        </div>
        
        <Show when={userStore.role === 'student'}>
            <div class="card mb-4">
                <div class="card-header">
                    {t("Class Information")}
                </div>
                <div class="card-body">
                    <div class="mb-3">
                        <label class="form-label">{t("Current Class/Promo")}</label>
                        <div class="d-flex align-items-center">
                            <Show 
                                when={userStore.promo_id} 
                                fallback={
                                    <span class="text-muted">{t("No class selected. Please select a class from the home page.")}</span>
                                }
                            >
                                <span class="me-3 p-2 bg-light border rounded">
                                    {userStore.promos_by_id[userStore.promo_id]?.name || t("Loading...")}
                                </span>
                                <button 
                                    type="button" 
                                    class="btn btn-outline-secondary" 
                                    onClick={handleLeavePromo}
                                >
                                    {t("Leave Class")}
                                </button>
                            </Show>
                        </div>
                        <div class="form-text">{t("Your class determines who can validate your internship agreements.")}</div>
                    </div>
                </div>
            </div>
        </Show>
        
        <div class="card">
            <div class="card-header">
                {t("Account Actions")}
            </div>
            <div class="card-body">
                <button 
                    type="button" 
                    class="btn btn-danger"
                    onClick={() => {
                        localStorage.removeItem('jwt');
                        location.reload();
                    }}
                >
                    {t("Logout")}
                </button>
            </div>
        </div>
    </div>
}